import React from 'react';

import { BaseTextField, FormControl, MaskedInput } from '@ifs-gcp/components';
import { AnswerTypes } from '@ifs-gcp/core/models';

import { useDebouncedInput } from '@/hooks/useDebouncedInput';
import type { AnswerProps } from '@/types/question';

export const TextAnswer = ({ answer, question, error, onBlur, onChange }: AnswerProps<string>) => {
  const {
    props: { id: questionId, variant, masks, placeholder, disabledCondition, readOnlyCondition },
  } = question;

  const {
    inputValue,
    handleChange,
    handleFocus: markAsFocused,
    handleBlur: resetInteractionState,
    isInteracting,
  } = useDebouncedInput({
    initialValue: answer,
    onChange,
  });

  // Force remount on external value changes to sync MaskedInput's internal state
  const remountVersionRef = React.useRef(0);
  const prevAnswerRef = React.useRef(answer);

  React.useEffect(() => {
    if (answer !== prevAnswerRef.current && !isInteracting) {
      remountVersionRef.current = remountVersionRef.current === 0 ? 1 : 0;
    }
    prevAnswerRef.current = answer;
  }, [answer, isInteracting]);

  const maskKey = `masked-${questionId}-v${remountVersionRef.current}`;

  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    resetInteractionState();

    const trimmedValue = event.target.value.trim();
    onChange(trimmedValue);
    onBlur?.(trimmedValue);
  };

  const isTextarea = variant === AnswerTypes.TEXTAREA;

  const params = {
    masks,
    value: inputValue,
    error: Boolean(error),
    placeholder,
    disabled: disabledCondition,
    readOnly: readOnlyCondition,
    multiline: isTextarea,
    minRows: isTextarea ? 3 : undefined,
    type: variant === AnswerTypes.NUMBER ? 'number' : 'text',
    onChange: handleChange,
    onFocus: markAsFocused,
    onBlur: handleBlur,
    inputProps: {
      id: questionId,
    },
  };

  return (
    <FormControl error={Boolean(error)} helperText={error}>
      {masks && masks.length > 0 ? (
        <MaskedInput {...params} masks={masks} key={maskKey} />
      ) : (
        <BaseTextField {...params} key={`text-${questionId}`} />
      )}
    </FormControl>
  );
};
